import React, { useEffect, useRef } from "react";
import BlinkerField from "../../blinkerInput/blinkerInput";
import GlowYearSelect from "../../glowYearDropdown/glowYearDropdown";
import GlowMonthSelect from "../../glowMonthDropDown/glowMonthDropDown";
import UpdateStateDropdown from "../../../components/updateStateSelect/updateStateSelect";
import DropDwnGlow from "../../dropDownGlow/dropDownGlow";
import { getStorageKey } from "../../../utils/localStorageStateUniqueKeyGenerator"; // this can also be used with cookie
import { paymentMethodOptions } from "../../../constants/dropdownOptions";
import { yearOptions } from "../../../constants/period";
import { ImgTopBtn } from "../../buttons";
import {
  HouseIcon,
  CreditCardWithLockIcon,
  UploadCloudIcon,
  PlusIcon,
} from "../../icons";
import { setCookie} from "../../../utils/cookies";
import "./billingUpdate.scss";

const BillingSettingForm = ({
  billingDetails,
  setBillingDetails,
  paymentMethod,
  setPaymentMethod,
  isPaymentMethodOpen,
  setPaymentMethodOpen,
  isMonthDropdownOpen,
  setMonthDropdownOpen,
  expirationMonth,
  setExpirationMonth,
  expirationYear,
  setExpirationYear,
  resetYearDropdown,
  onYearDropdownReset,
  creditCardNumber,
  setCreditCardNumber,
  selectedState,
  setSelectedState,
  isYearDropdownOpen,
  setYearDropdownOpen,
  isStateDropdownOpen,
  setStateDropdownOpen,
  fieldErrorMessage,
  currentContextType,
  updating,
  showCardSection,
  setShowCardSection,
  showBillingSection,
  setShowBillingSection,
  setCurrentContextType,
  handleMemberWallet,
}) => {
  

  
  // Watcher functions for updating state and localStorage
  const watcherPaymentMethodChange = (e) => {
    // in my DropDwnGlow component, you’re calling onChange like this:
    // onChange({ target: { value: option.value } });
    //  the watcher receives an object that mimics an event.
    // So the watcher extracts the value with the event instead of the whole array:
    const paymentMethod = e.target.value;
    setPaymentMethod(paymentMethod);
    setCookie(
      getStorageKey("paymentMethod", currentContextType),
      paymentMethod
    );
  };

  const watcherExpirationMonthChange = (newMonth) => {
    // called with a primitive value so no need to assign the event
    setExpirationMonth(newMonth);
    setCookie(
      getStorageKey("ccExpMonth", currentContextType),
      newMonth
    );
  };

  const watcherExpirationYearChange = (e) => {
    const ccExpireYear = e.target.value;
    setExpirationYear(ccExpireYear);
    setCookie(
      getStorageKey("ccExpYear", currentContextType),
      ccExpireYear
    );
  };

  const watcherCreditCardChange = (e) => {
    const newValue = e.target.value;
    setCreditCardNumber(newValue);
    setCookie(getStorageKey("creditCardNumber", currentContextType), newValue, 1);
  };
  
  const watcherBillingDetailsChange = (field, e) => {
    const newValue = e.target.value;
    const updatedDetails = { ...billingDetails, [field]: newValue };
    setBillingDetails(updatedDetails);
    // Store the updated field in a cookie
    setCookie(getStorageKey(field, currentContextType), newValue, 1);
  };

  const watcherSelectedStateChange = (newState) => {
    setSelectedState(newState);
    // Use the key "billingState" consistently
    setCookie(getStorageKey("billingState", currentContextType), newState);
    // Update the billingDetails with "billingState" property
    setBillingDetails((prev) => ({ ...prev, billingState: newState }));
  };
  
  const billingEndRef = useRef(null);

  useEffect(() => {
    if (showBillingSection && billingEndRef.current) {
      billingEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [showBillingSection]);

  return (
    <div className="billing-update-wrapper">
      <div className="billing-update-content">
        <h4 className="update-billing-header billingInstructions">
          Click on a button to enter details
        </h4>
        {/* Toggle Card Section */}
        <div className="update-billing-header">
          <ImgTopBtn
            image={
              <CreditCardWithLockIcon cardHeight="15px" cardWidth="15px" />
            }
            onClick={() => {
              const newValue = !showCardSection;
              setShowCardSection(newValue);
              const key = `${currentContextType}_CardSection`;
              setCookie(key, newValue.toString());
            }}
            imgTopBtnText="Press & Enter Card Info"
            imgTopBtnTextFontSize="1rem"
            imgTopBtnFontColor="var(--color-bloodred)"
            className="add_btn"
            paddingRight="10px"
            paddingLeft="10px"
          />
        </div>
        {showCardSection && (
          <>
            <DropDwnGlow
              id="cardTopField"
              options={paymentMethodOptions}
              selectedOption={paymentMethod}
              onChange={watcherPaymentMethodChange} // Now passing event-like object
              placeholder={
                fieldErrorMessage.paymentMethod || "Select Payment Method"
              }
              isOpen={isPaymentMethodOpen}
              setOpen={setPaymentMethodOpen}
              onClick={(e) => {
                console.log("Parent onClick triggered", e);
              }}
              className={`textBox ${fieldErrorMessage.paymentMethod && !isPaymentMethodOpen ? "billing-error-border" : ""}`}
            />
            <div className="credit-card-expiry-container">
              <GlowMonthSelect
                value={expirationMonth}
                onChange={(value) => watcherExpirationMonthChange(value)}
                isOpen={isMonthDropdownOpen}
                onToggle={setMonthDropdownOpen}
                fieldError={fieldErrorMessage.expirationMonth}
                className={`${fieldErrorMessage.expirationMonth ? "billing-error-border" : ""}`}
                currentContextType={currentContextType}
              />
              <GlowYearSelect
                value={expirationYear}
                onChange={watcherExpirationYearChange}
                options={yearOptions}
                isOpen={isYearDropdownOpen}
                onToggle={setYearDropdownOpen}
                resetYearDropdown={resetYearDropdown}
                onResetYearComplete={onYearDropdownReset}
                fieldError={fieldErrorMessage.expirationYear}
                className={`${fieldErrorMessage.expirationYear ? "billing-error-border" : ""}`}
                currentContextType={currentContextType}
              />
            </div>
            <BlinkerField
              className={`creditcard-number-neumorphism-field ${fieldErrorMessage.creditCardNumber ? "billing-error-border" : ""}`}
              placeholder={
                fieldErrorMessage.creditCardNumber || "Enter Credit Card Number"
              }
              type="text"
              value={creditCardNumber}
              onChange={(e) => watcherCreditCardChange(e)}
            />
          </>
        )}

        {/* Toggle Billing Section */}
        <div className="update-billing-header">
          <ImgTopBtn
            image={<HouseIcon houseHeight="15px" houseWidth="15px" />}
            imgTopBtnText="Press & Enter Billing Info"
            imgTopBtnTextFontSize="1rem"
            imgTopBtnFontColor="var(--color-bloodred)"
            className="add_btn"
            paddingLeft="10px"
            onClick={() => {
              const newValue = !showBillingSection;
              setShowBillingSection(newValue);
              const key = `${currentContextType}_BillingSection`;
              setCookie(key, newValue.toString());
            }}
          />
        </div>
        {showBillingSection && (
          <>
            <BlinkerField
              className={`name-neumorphism-field ${
                fieldErrorMessage.billingFirstName ? "billing-error-border" : ""
              }`}
              placeholder={
                fieldErrorMessage.billingFirstName || "Enter First Legal Name"
              }
              type="text"
              value={billingDetails.billingFirstName}
              onChange={(e) =>
                watcherBillingDetailsChange("billingFirstName", e)
              }
            />

            <BlinkerField
              className={`name-neumorphism-field ${
                fieldErrorMessage.billingLastName ? "billing-error-border" : ""
              }`}
              placeholder={
                fieldErrorMessage.billingLastName || "Enter Last Legal Name"
              }
              type="text"
              value={billingDetails.billingLastName}
              onChange={(e) =>
                watcherBillingDetailsChange("billingLastName", e)
              }
            />

            <BlinkerField
              className={`name-neumorphism-field ${
                fieldErrorMessage.billingAddressLine1
                  ? "billing-error-border"
                  : ""
              }`}
              placeholder={
                fieldErrorMessage.billingAddressLine1 || "Enter Address Line 1"
              }
              type="text"
              value={billingDetails.billingAddressLine1}
              onChange={(e) =>
                watcherBillingDetailsChange("billingAddressLine1", e)
              }
            />

            <BlinkerField
              className="name-neumorphism-field"
              placeholder="Address Line 2 (ex. Apartment, suite, unit, building, or floor numbers)"
              type="text"
              value={billingDetails.billingAddressLine2}
              onChange={(e) =>
                watcherBillingDetailsChange("billingAddressLine2", e)
              }
            />

            <BlinkerField
              className={`name-neumorphism-field ${
                fieldErrorMessage.billingCity ? "billing-error-border" : ""
              }`}
              placeholder={fieldErrorMessage.billingCity || "Enter City"}
              type="text"
              value={billingDetails.billingCity}
              onChange={(e) => watcherBillingDetailsChange("billingCity", e)}
            />

            <BlinkerField
              className={`name-neumorphism-field ${
                fieldErrorMessage.billingZipCode ? "billing-error-border" : ""
              }`}
              placeholder={
                fieldErrorMessage.billingZipCode || "Enter Billing Zip Code"
              }
              type="text"
              value={billingDetails.billingZipCode}
              onChange={(e) => watcherBillingDetailsChange("billingZipCode", e)}
            />

            <BlinkerField
              className={`name-neumorphism-field ${
                fieldErrorMessage.billingCountry ? "billing-error-border" : ""
              }`}
              placeholder={
                fieldErrorMessage.billingCountry || "Enter Billing Country"
              }
              type="text"
              value={billingDetails.billingCountry}
              onChange={(e) => watcherBillingDetailsChange("billingCountry", e)}
            />

            <UpdateStateDropdown
              selectedState={selectedState}
              clearState={() => watcherSelectedStateChange("")}
              setSelectedState={(newVal) => watcherSelectedStateChange(newVal)}
              isOpen={isStateDropdownOpen}
              onToggle={setStateDropdownOpen}
              placeholder={
                fieldErrorMessage.billingState || "Select Billing State"
              }
              className={`billing-statedropdown ${
                fieldErrorMessage.billingState && !isStateDropdownOpen
                  ? "billing-error-border"
                  : ""
              }`}
            />
          </>
        )}

        {(showCardSection || showBillingSection) && (
          <div className="btn-container" ref={billingEndRef}>
            {updating ? (
              <ImgTopBtn
                image={
                  <UploadCloudIcon
                    height="15px"
                    width="15px"
                    fillColor="#b03a2e"
                  />
                }
                imgTopBtnText="Update Payment Details"
                imgTopBtnTextFontSize="1rem"
                imgTopBtnFontColor="var(--color-bloodred)"
                className="history_btn"
                paddingRight="10px"
                paddingLeft="10px"
                onClick={() => {
                  setCurrentContextType("update");
                  handleMemberWallet();
                }}
              />
            ) : (
              <ImgTopBtn
                image={
                  <PlusIcon height="15px" width="15px" fillColor="#b03a2e" />
                }
                imgTopBtnText="Add Payment & Billing"
                imgTopBtnTextFontSize="1rem"
                imgTopBtnFontColor="var(--color-bloodred)"
                className="add_btn"
                paddingRight="10px"
                paddingLeft="10px"
                onClick={() => {
                  setCurrentContextType("create");
                  handleMemberWallet();
                }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingSettingForm;
